import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { CheckCircle, FileText, Shield, Lock, AlertTriangle, Scale, Database, Globe, Clock, UserCheck, Server, Edit, Gavel } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsOfService = ({ user, onAccept }) => {
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleAccept = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/users/${user.user_id}/accept-terms`);
      toast.success('Οι όροι χρήσης έγιναν αποδεκτοί');
      onAccept();
    } catch (error) {
      toast.error('Σφάλμα κατά την αποδοχή των όρων');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8" />
            <h1 className="text-2xl font-bold">Όροι Χρήσης Εφαρμογής</h1>
          </div>
          <p className="text-emerald-100 text-sm">
            Παρακαλούμε διαβάστε προσεκτικά και αποδεχτείτε τους όρους χρήσης για να συνεχίσετε
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="h-[450px] overflow-y-auto border border-slate-200 rounded-lg p-5 bg-slate-50">
            <div className="prose prose-sm max-w-none text-slate-700 space-y-6">
              
              {/* Εισαγωγή */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <FileText className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΟΡΟΙ ΧΡΗΣΗΣ ΕΦΑΡΜΟΓΗΣ</h2>
                </div>
                <p className="text-justify">
                  Η παρούσα εφαρμογή παρέχεται ως υπηρεσία λογισμικού (Software as a Service – SaaS) και έχει ως σκοπό την υποστήριξη της οργάνωσης, διαχείρισης, επεξεργασίας και αυτοματοποιημένης ανάλυσης δεδομένων και εγγράφων που σχετίζονται με προγράμματα επιδοτήσεων και συγχρηματοδοτούμενες δράσεις.
                </p>
                <p className="text-justify">
                  Η πρόσβαση και χρήση της εφαρμογής προϋποθέτουν την πλήρη, ρητή και ανεπιφύλακτη αποδοχή των παρόντων Όρων Χρήσης από τον χρήστη. <strong>Σε περίπτωση μη αποδοχής οποιουδήποτε όρου, ο χρήστης οφείλει να απέχει από οποιαδήποτε χρήση της εφαρμογής.</strong>
                </p>
              </section>

              {/* Πολιτική Απορρήτου */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Shield className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΠΟΛΙΤΙΚΗ ΑΠΟΡΡΗΤΟΥ (PRIVACY POLICY)</h2>
                </div>
                <p className="text-justify">
                  Η παρούσα Πολιτική Απορρήτου περιγράφει τον τρόπο με τον οποίο η εταιρεία συλλέγει, επεξεργάζεται, αποθηκεύει και προστατεύει δεδομένα προσωπικού χαρακτήρα που παρέχονται από τους χρήστες κατά τη χρήση της εφαρμογής, σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (ΕΕ) 2016/679 (GDPR), την ελληνική και την ενωσιακή νομοθεσία.
                </p>
                <p className="text-justify">
                  Η πρόσβαση και η χρήση της εφαρμογής συνεπάγονται την πλήρη, ρητή και ανεπιφύλακτη αποδοχή της παρούσας Πολιτικής Απορρήτου. Σε περίπτωση μη αποδοχής, ο χρήστης οφείλει να απέχει από οποιαδήποτε χρήση της εφαρμογής.
                </p>
              </section>

              {/* Υπεύθυνος Επεξεργασίας */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <UserCheck className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΥΠΕΥΘΥΝΟΣ ΕΠΕΞΕΡΓΑΣΙΑΣ</h2>
                </div>
                <p className="text-justify">
                  Υπεύθυνος επεξεργασίας των δεδομένων είναι η εταιρεία που παρέχει την εφαρμογή. Ανάλογα με τη φύση των δεδομένων και τον τρόπο χρήσης της υπηρεσίας, η εταιρεία δύναται να ενεργεί είτε ως Υπεύθυνος Επεξεργασίας, είτε ως Εκτελών την Επεξεργασία, σύμφωνα με το άρθρο 28 GDPR, ιδίως όταν τα δεδομένα εισάγονται και ελέγχονται αποκλειστικά από τον χρήστη.
                </p>
              </section>

              {/* Κατηγορίες Δεδομένων */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Database className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΚΑΤΗΓΟΡΙΕΣ ΔΕΔΟΜΕΝΩΝ ΠΟΥ ΕΠΕΞΕΡΓΑΖΟΜΑΣΤΕ</h2>
                </div>
                <p className="text-justify">
                  Κατά τη χρήση της εφαρμογής ενδέχεται να γίνεται επεξεργασία των ακόλουθων κατηγοριών δεδομένων:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-justify">
                  <li>Δεδομένα ταυτοποίησης και επικοινωνίας χρηστών (όνομα, επώνυμο, email, τηλέφωνο, επαγγελματική ιδιότητα)</li>
                  <li>Δεδομένα σύνδεσης και χρήσης της εφαρμογής (IP διεύθυνση, ημερομηνίες και ώρες πρόσβασης, logs συστήματος)</li>
                  <li>Δεδομένα και έγγραφα που μεταφορτώνονται ή καταχωρούνται από τον ίδιο τον χρήστη στο πλαίσιο χρήσης της εφαρμογής, τα οποία ενδέχεται να περιλαμβάνουν δεδομένα φυσικών προσώπων, νομικών προσώπων ή τρίτων</li>
                </ul>
                <p className="text-justify mt-3">
                  Η εταιρεία δεν απαιτεί, ούτε επιδιώκει, την καταχώρηση ειδικών κατηγοριών δεδομένων (ευαίσθητων δεδομένων κατά το άρθρο 9 GDPR). Σε περίπτωση που τέτοια δεδομένα καταχωρηθούν από τον χρήστη, αυτό γίνεται με αποκλειστική ευθύνη του τελευταίου.
                </p>
              </section>

              {/* Σκοποί και Νομική Βάση */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Scale className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΣΚΟΠΟΙ ΚΑΙ ΝΟΜΙΚΗ ΒΑΣΗ ΕΠΕΞΕΡΓΑΣΙΑΣ</h2>
                </div>
                <p className="text-justify">Η επεξεργασία των δεδομένων πραγματοποιείται αποκλειστικά για τους ακόλουθους σκοπούς:</p>
                <ul className="list-disc pl-5 space-y-1 text-justify">
                  <li>Την παροχή και εύρυθμη λειτουργία της εφαρμογής</li>
                  <li>Την τεχνική υποστήριξη και συντήρηση της υπηρεσίας</li>
                  <li>Την ασφάλεια των συστημάτων και την πρόληψη κακόβουλων ενεργειών</li>
                  <li>Την αυτοματοποιημένη επεξεργασία εγγράφων και δεδομένων σύμφωνα με τις επιλογές του χρήστη</li>
                  <li>Τη συμμόρφωση με νομικές υποχρεώσεις</li>
                </ul>
                <p className="text-justify mt-3">
                  Η νομική βάση της επεξεργασίας είναι, κατά περίπτωση, η εκτέλεση σύμβασης, η συμμόρφωση με έννομη υποχρέωση, το έννομο συμφέρον της εταιρείας για την ασφάλεια και λειτουργικότητα της εφαρμογής, καθώς και η συγκατάθεση του χρήστη όπου απαιτείται.
                </p>
              </section>

              {/* AI */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Server className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΑΥΤΟΜΑΤΟΠΟΙΗΜΕΝΗ ΕΠΕΞΕΡΓΑΣΙΑ & AI</h2>
                </div>
                <p className="text-justify">
                  Η εφαρμογή ενδέχεται να χρησιμοποιεί αυτοματοποιημένες διαδικασίες και αλγορίθμους για την ανάλυση, κατηγοριοποίηση και υποστήριξη ελέγχου εγγράφων και δεδομένων. Οι διαδικασίες αυτές έχουν αποκλειστικά υποστηρικτικό χαρακτήρα και δεν παράγουν δεσμευτικές αποφάσεις που παράγουν έννομα αποτελέσματα για τον χρήστη ή τρίτους.
                </p>
                <p className="text-justify">
                  Η εταιρεία δεν λαμβάνει αυτοματοποιημένες αποφάσεις κατά την έννοια του άρθρου 22 GDPR.
                </p>
              </section>

              {/* Πρόσβαση στα Δεδομένα */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Lock className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΠΡΟΣΒΑΣΗ ΣΤΑ ΔΕΔΟΜΕΝΑ</h2>
                </div>
                <p className="text-justify">
                  Η εταιρεία δύναται να έχει πρόσβαση στα δεδομένα που καταχωρούνται στην εφαρμογή αποκλειστικά στον βαθμό που αυτό είναι απολύτως απαραίτητο για λόγους τεχνικής υποστήριξης, ασφάλειας, διάγνωσης και επίλυσης τεχνικών προβλημάτων και εύρυθμης λειτουργίας της υπηρεσίας.
                </p>
                <p className="text-justify">
                  Η πρόσβαση αυτή είναι περιορισμένη, ελεγχόμενη και καταγεγραμμένη και δεν πραγματοποιείται για εμπορικούς σκοπούς.
                </p>
              </section>

              {/* Αποθήκευση & Backup */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Database className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΑΠΟΘΗΚΕΥΣΗ & BACKUP</h2>
                </div>
                <p className="text-justify">
                  Τα δεδομένα αποθηκεύονται σε ασφαλή συστήματα. Η εφαρμογή ενδέχεται να παρέχει μηχανισμούς δημιουργίας αντιγράφων ασφαλείας (backup) αποκλειστικά για λόγους τεχνικής ασφάλειας και συνέχειας της υπηρεσίας. Τα αντίγραφα ασφαλείας δεν υποκαθιστούν την υποχρέωση του χρήστη να διατηρεί δικά του ανεξάρτητα αντίγραφα.
                </p>
                <p className="text-justify">
                  <strong>Η εταιρεία δεν φέρει ευθύνη για απώλεια ή αλλοίωση δεδομένων που ενδέχεται να προκύψει λόγω αναβαθμίσεων, τεχνικών εργασιών, αστοχιών ή γεγονότων ανωτέρας βίας.</strong>
                </p>
              </section>

              {/* Διαβίβαση Δεδομένων */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Globe className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΔΙΑΒΙΒΑΣΗ ΔΕΔΟΜΕΝΩΝ</h2>
                </div>
                <p className="text-justify">
                  Τα δεδομένα δεν διαβιβάζονται σε τρίτους, εκτός εάν αυτό απαιτείται για την παροχή της υπηρεσίας (π.χ. πάροχοι υποδομών), ή επιβάλλεται από τον νόμο. Σε κάθε περίπτωση διασφαλίζεται ότι οι τρίτοι συμμορφώνονται με τον GDPR.
                </p>
                <p className="text-justify">
                  Δεν πραγματοποιείται διαβίβαση δεδομένων εκτός Ευρωπαϊκής Ένωσης χωρίς επαρκείς εγγυήσεις.
                </p>
              </section>

              {/* Χρόνος Τήρησης */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Clock className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΧΡΟΝΟΣ ΤΗΡΗΣΗΣ</h2>
                </div>
                <p className="text-justify">
                  Τα δεδομένα τηρούνται για όσο χρονικό διάστημα είναι απαραίτητο για την παροχή της υπηρεσίας και σύμφωνα με τις νόμιμες υποχρεώσεις της εταιρείας ή έως ότου ζητηθεί η διαγραφή τους από τον χρήστη, όπου αυτό είναι επιτρεπτό.
                </p>
              </section>

              {/* Δικαιώματα Υποκειμένων */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <UserCheck className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΔΙΚΑΙΩΜΑΤΑ ΥΠΟΚΕΙΜΕΝΩΝ</h2>
                </div>
                <p className="text-justify">
                  Ο χρήστης διατηρεί όλα τα δικαιώματα που απορρέουν από τον GDPR, συμπεριλαμβανομένων των δικαιωμάτων πρόσβασης, διόρθωσης, διαγραφής, περιορισμού επεξεργασίας, φορητότητας και εναντίωσης, όπου αυτά εφαρμόζονται. Τα αιτήματα ασκούνται εγγράφως και εξετάζονται σύμφωνα με τον νόμο.
                </p>
              </section>

              {/* Ασφάλεια */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Shield className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΑΣΦΑΛΕΙΑ</h2>
                </div>
                <p className="text-justify">
                  Η εταιρεία λαμβάνει τα κατάλληλα τεχνικά και οργανωτικά μέτρα για την προστασία των δεδομένων, χωρίς ωστόσο να εγγυάται απόλυτη ασφάλεια λόγω της φύσης του διαδικτύου.
                </p>
              </section>

              {/* Τροποποιήσεις */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Edit className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΤΡΟΠΟΠΟΙΗΣΕΙΣ</h2>
                </div>
                <p className="text-justify">
                  Η εταιρεία διατηρεί το δικαίωμα να τροποποιεί την παρούσα Πολιτική Απορρήτου οποτεδήποτε. Οι αλλαγές ισχύουν από τη δημοσίευσή τους στην εφαρμογή ή στον ιστότοπο.
                </p>
              </section>

              {/* Εφαρμοστέο Δίκαιο */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Gavel className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΕΦΑΡΜΟΣΤΕΟ ΔΙΚΑΙΟ</h2>
                </div>
                <p className="text-justify">
                  Η παρούσα Πολιτική Απορρήτου διέπεται από το Ελληνικό Δίκαιο. Αρμόδια για την επίλυση οποιασδήποτε διαφοράς είναι τα δικαστήρια της έδρας της εταιρείας.
                </p>
              </section>

              {/* Περιορισμός Ευθύνης - Σημαντική Ενότητα */}
              <section className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 mb-3">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΣΗΜΑΝΤΙΚΗ ΔΗΛΩΣΗ - ΠΕΡΙΟΡΙΣΜΟΣ ΕΥΘΥΝΗΣ</h2>
                </div>
                <p className="text-justify text-amber-900">
                  Η εφαρμογή λειτουργεί αποκλειστικά ως υποστηρικτικό εργαλείο. <strong>Δεν αποτελεί δημόσια υπηρεσία, δεν φέρει καμία θεσμική ή διοικητική αρμοδιότητα, δεν υποκαθιστά οποιοδήποτε πληροφοριακό σύστημα δημόσιου φορέα, συμπεριλαμβανομένου ενδεικτικά του ΟΠΣΚΕ, και δεν αντικαθιστά τον επαγγελματικό, διοικητικό, λογιστικό ή νομικό έλεγχο που οφείλει να διενεργεί ο χρήστης ή οι συνεργάτες του.</strong> Η εφαρμογή δεν εγκρίνει, δεν απορρίπτει και δεν πιστοποιεί αιτήσεις, δαπάνες ή δικαιολογητικά.
                </p>
                <p className="text-justify text-amber-900 mt-3">
                  Οποιαδήποτε αποτελέσματα, ενδείξεις, ειδοποιήσεις, χαρακτηρισμοί, υπολογισμοί, ποσοστά, αναφορές, προειδοποιήσεις ή συμπεράσματα που παράγονται μέσω της εφαρμογής, ανεξαρτήτως μορφής ή ονομασίας, συμπεριλαμβανομένων ενδεικτικά αποτελεσμάτων τύπου PASS/FAIL, ποσοστών κάλυψης κατηγοριών δαπάνης, υπολειπόμενων ποσών ή υπερβάσεων, <strong>έχουν αποκλειστικά ενημερωτικό και υποστηρικτικό χαρακτήρα και δεν συνιστούν δεσμευτικό έλεγχο, επίσημη γνωμοδότηση, διαβεβαίωση επιλεξιμότητας ή εγγύηση αποτελέσματος.</strong>
                </p>
                <p className="text-justify text-amber-900 mt-3">
                  <strong>Η τελική ευθύνη για την αξιολόγηση, ερμηνεία, πληρότητα, ακρίβεια και υποβολή των στοιχείων ανήκει αποκλειστικά στον χρήστη.</strong>
                </p>
              </section>

              {/* Αποποίηση Εγγυήσεων */}
              <section className="bg-red-50 p-4 rounded-lg border border-red-200 mt-4">
                <div className="flex items-center gap-2 text-red-700 mb-3">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΑΠΟΠΟΙΗΣΗ ΕΓΓΥΗΣΕΩΝ</h2>
                </div>
                <p className="text-justify text-red-900">
                  Η εταιρεία δεν παρέχει καμία απολύτως εγγύηση, ρητή ή σιωπηρή, αναφορικά με την έγκριση αιτήσεων, την αποδοχή δικαιολογητικών, την αποφυγή απορρίψεων, περικοπών, παρατηρήσεων, κυρώσεων ή οποιασδήποτε άλλης διοικητικής ή οικονομικής συνέπειας.
                </p>
                <p className="text-justify text-red-900 mt-3">
                  <strong>Ο χρήστης αποδέχεται ρητά ότι η χρήση της εφαρμογής γίνεται με δική του αποκλειστική ευθύνη και ότι η εταιρεία δεν φέρει καμία ευθύνη, άμεση ή έμμεση, για οποιαδήποτε ζημία, απώλεια επιδότησης, απώλεια εσόδων, οικονομική απώλεια, καθυστέρηση, απόρριψη αιτήσεων, τροποποίηση αποφάσεων ή επιβολή κυρώσεων που προκύπτουν από τη χρήση ή τη μη χρήση της εφαρμογής, ακόμη και αν ο χρήστης έχει βασιστεί σε αποτελέσματα ή ενδείξεις που παρήχθησαν από αυτήν.</strong>
                </p>
              </section>

              {/* Υπολογισμοί & Όρια */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Scale className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΥΠΟΛΟΓΙΣΜΟΙ & ΟΡΙΑ ΔΑΠΑΝΩΝ</h2>
                </div>
                <p className="text-justify">
                  Οι έλεγχοι, τα πρότυπα, οι κανόνες, τα templates, οι υπολογισμοί και τα reports της εφαρμογής βασίζονται αποκλειστικά στα δεδομένα που εισάγει ο ίδιος ο χρήστης και στις πληροφορίες που έχουν ενσωματωθεί στο σύστημα κατά τον χρόνο χρήσης.
                </p>
                <p className="text-justify mt-3">
                  Οι προσκλήσεις, οι κανονισμοί, οι οδηγοί εφαρμογής, οι εγκύκλιοι και το θεσμικό πλαίσιο των προγραμμάτων επιδοτήσεων δύνανται να τροποποιούνται οποτεδήποτε από τις αρμόδιες αρχές χωρίς προηγούμενη ειδοποίηση. Η εταιρεία δεν υποχρεούται να παρακολουθεί σε πραγματικό χρόνο κάθε τέτοια μεταβολή και δεν φέρει καμία ευθύνη για ασυμφωνίες, αποκλίσεις ή ερμηνείες που προκύπτουν λόγω μεταγενέστερων αλλαγών.
                </p>
                <p className="text-justify mt-3">
                  Οποιαδήποτε λειτουργία σχετική με τον υπολογισμό ορίων δαπανών, ποσοστών κάλυψης, υπολειπόμενων ποσών ή υπερβάσεων παρέχεται αποκλειστικά για σκοπούς υποστήριξης και πρόληψης λαθών και δεν αποτελεί τελικό ή δεσμευτικό έλεγχο επιλεξιμότητας. <strong>Η εφαρμογή δεν μπλοκάρει ενέργειες του χρήστη και δεν φέρει καμία ευθύνη για υπερβάσεις ή αποκλίσεις που εντοπίζονται σε μεταγενέστερο διοικητικό ή οικονομικό έλεγχο.</strong>
                </p>
              </section>

              {/* Υποχρεώσεις Χρήστη */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΥΠΟΧΡΕΩΣΕΙΣ ΧΡΗΣΤΗ</h2>
                </div>
                <p className="text-justify">
                  Ο χρήστης δηλώνει και εγγυάται ότι τα δεδομένα και τα έγγραφα που καταχωρεί στην εφαρμογή είναι νόμιμα, ακριβή και ότι διαθέτει κάθε απαιτούμενο δικαίωμα για τη χρήση τους. Ο χρήστης αποδέχεται ότι η εταιρεία δεν υποχρεούται να ελέγχει τη νομιμότητα, την ακρίβεια ή τη γνησιότητα των δεδομένων αυτών.
                </p>
                <p className="text-justify mt-3">
                  <strong>Ο χρήστης συμφωνεί να αποζημιώσει και να απαλλάξει πλήρως την εταιρεία, τα στελέχη, τους συνεργάτες και τους παρόχους της από οποιαδήποτε απαίτηση, ζημία, αξίωση, πρόστιμο, κύρωση ή δαπάνη (συμπεριλαμβανομένων δικαστικών και εξώδικων εξόδων) που προκύπτει από τη χρήση της εφαρμογής, την παραβίαση των παρόντων όρων, την παραβίαση νόμων ή δικαιωμάτων τρίτων ή την υποβολή ανακριβών ή παράνομων στοιχείων.</strong>
                </p>
              </section>

              {/* Πνευματική Ιδιοκτησία */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Shield className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΠΝΕΥΜΑΤΙΚΗ ΙΔΙΟΚΤΗΣΙΑ</h2>
                </div>
                <p className="text-justify">
                  Όλο το λογισμικό, η αρχιτεκτονική, ο πηγαίος και εκτελέσιμος κώδικας, οι αλγόριθμοι, τα πρότυπα, οι κανόνες, τα reports, τα αποτελέσματα και η συνολική λογική λειτουργίας της εφαρμογής αποτελούν αποκλειστική πνευματική ιδιοκτησία της εταιρείας και προστατεύονται από την ισχύουσα νομοθεσία περί πνευματικής ιδιοκτησίας και αθέμιτου ανταγωνισμού.
                </p>
                <p className="text-justify mt-3">
                  <strong>Απαγορεύεται ρητά οποιαδήποτε αντιγραφή, αναπαραγωγή, απομίμηση, αποσυμπίληση, αντίστροφη μηχανική ή χρήση της εφαρμογής ή μέρους αυτής για την ανάπτυξη παρόμοιου ή ανταγωνιστικού συστήματος.</strong>
                </p>
              </section>

              {/* Απαγορευμένες Χρήσεις */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΑΠΑΓΟΡΕΥΜΕΝΕΣ ΧΡΗΣΕΙΣ</h2>
                </div>
                <p className="text-justify">
                  Απαγορεύεται η χρήση της εφαρμογής για παράνομες, παραπλανητικές ή καταχρηστικές ενέργειες, καθώς και για την απόπειρα παράκαμψης ελέγχων δημόσιων αρχών ή τη μαζική, αυτοματοποιημένη χρήση χωρίς σχετική άδεια.
                </p>
                <p className="text-justify mt-3">
                  Η εταιρεία διατηρεί το δικαίωμα να περιορίσει ή να αναστείλει την πρόσβαση χρήστη σε περίπτωση παραβίασης των παρόντων όρων.
                </p>
              </section>

              {/* Τροποποιήσεις Υπηρεσίας */}
              <section>
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Edit className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΤΡΟΠΟΠΟΙΗΣΕΙΣ ΥΠΗΡΕΣΙΑΣ</h2>
                </div>
                <p className="text-justify">
                  Η εταιρεία διατηρεί το δικαίωμα να τροποποιεί, αναβαθμίζει, περιορίζει ή διακόπτει προσωρινά ή μόνιμα τη λειτουργία της εφαρμογής ή μέρους αυτής, χωρίς προηγούμενη ειδοποίηση και χωρίς οποιαδήποτε υποχρέωση αποζημίωσης.
                </p>
                <p className="text-justify mt-3">
                  Τυχόν δοκιμαστική ή δωρεάν περίοδος χρήσης παρέχεται χωρίς καμία εγγύηση αποτελέσματος και μπορεί να τροποποιηθεί ή να διακοπεί οποτεδήποτε.
                </p>
              </section>

              {/* Τελικές Διατάξεις */}
              <section className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <Gavel className="h-5 w-5 flex-shrink-0" />
                  <h2 className="text-lg font-semibold m-0">ΤΕΛΙΚΕΣ ΔΙΑΤΑΞΕΙΣ</h2>
                </div>
                <p className="text-justify text-emerald-900">
                  Οι παρόντες Όροι Χρήσης διέπονται από το Ελληνικό Δίκαιο. Αρμόδια για την επίλυση οποιασδήποτε διαφοράς που απορρέει από τη χρήση της εφαρμογής είναι αποκλειστικά τα δικαστήρια της έδρας της εταιρείας.
                </p>
              </section>

            </div>
          </div>

          {/* Accept Section */}
          <div className="mt-6 space-y-4">
            {/* Agreement checkbox */}
            <label 
              className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
              data-testid="agree-checkbox-label"
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="h-5 w-5 mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="agree-checkbox"
              />
              <span className="text-slate-700">
                <strong>Δηλώνω ότι έχω διαβάσει, κατανοήσει και αποδέχομαι πλήρως, ρητά και ανεπιφύλακτα τους παραπάνω Όρους Χρήσης και την Πολιτική Απορρήτου της εφαρμογής.</strong>
              </span>
            </label>
            
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Συνδεδεμένος ως: <span className="font-medium">{user.email}</span>
              </p>
              <Button
                onClick={handleAccept}
                disabled={!agreed || loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="accept-terms-btn"
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Αποδοχή...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Συνέχεια στην Εφαρμογή
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
